// ntfy push notification subscription (dev8 Phase 2 P4).
//
// Self-hosted ntfy (hub-ntfy) replaces Firebase/Google push. The user
// explicitly clicks "Enable Push Notifications" to subscribe — we NEVER
// auto-subscribe. Topic per person: `hub-{person_id}`.
//
// Flow:
//   1. User clicks "Enable Push" button in Settings > Notifications.
//   2. We ask for browser Notification permission (gesture-synchronous).
//   3. We open an EventSource to /ntfy/hub-{personId}/sse to receive
//      server-sent events from ntfy. This is the simplest web-push path
//      that doesn't require a service worker or VAPID keys.
//   4. On each event, we fire a browser Notification via the existing
//      notify() helper.
//
// Persistence: subscription state stored in localStorage so we can
// reconnect on page load if the user previously opted in.

import { state } from '../state';
import { notify, requestNotificationPermissionSync } from './notifications';

const NTFY_SUB_KEY = 'hub-people-ntfy-subscribed';
const PUSH_BANNER_DISMISSED_KEY = 'hub-people-pushbanner-dismissed';
let eventSource: EventSource | null = null;
let pushBannerFired = false;

/** Whether the user has opted in to ntfy push. */
export function isNtfySubscribed(): boolean {
  try { return localStorage.getItem(NTFY_SUB_KEY) === '1'; } catch { return false; }
}

/** Subscribe to ntfy push for the current user. Call from a click handler. */
export function subscribeNtfy(): void {
  if (!state.ownerId) return;
  try { localStorage.setItem(NTFY_SUB_KEY, '1'); } catch { /* ignore */ }
  connectNtfySse();
}

/** Unsubscribe from ntfy push. */
export function unsubscribeNtfy(): void {
  try { localStorage.setItem(NTFY_SUB_KEY, '0'); } catch { /* ignore */ }
  disconnectNtfySse();
}

/** Reconnect if previously subscribed. Called from app init. */
export function reconnectNtfyIfSubscribed(): void {
  if (isNtfySubscribed() && state.ownerId) {
    connectNtfySse();
  }
}

function connectNtfySse(): void {
  disconnectNtfySse();
  if (!state.ownerId) return;
  const topic = `hub-${state.ownerId}`;
  // Use the hub-core proxy path — session cookie authenticates.
  const url = `/ntfy/${topic}/sse`;
  try {
    eventSource = new EventSource(url);
    eventSource.onmessage = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data as string) as {
          event?: string;
          title?: string;
          message?: string;
        };
        if (data.event !== 'message') return;
        notify({
          kind: 'mention',
          title: data.title ?? 'Notification',
          body: data.message ?? '',
        });
      } catch { /* ignore malformed events */ }
    };
    eventSource.onerror = (): void => {
      // SSE auto-reconnects; no action needed. If the connection is
      // permanently broken the browser will retry with backoff.
    };
  } catch { /* ignore — EventSource not supported or URL blocked */ }
}

function disconnectNtfySse(): void {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
}

/**
 * One-time nudge to turn web push ON. Modelled on the reload banner
 * (`showUpdateBanner` in app.ts): a full-width dismissible TOP HEADER row, not a
 * floating toast. Wired to fire on the first new message (see app.ts), but it
 * only ever shows ONCE — a session flag guards against per-message spam, it's
 * skipped entirely if push is already enabled, and a dismissal is persisted so
 * it never nags again. "Turn on" runs inside the click gesture: request the
 * browser Notification permission, then subscribe to self-hosted ntfy push.
 */
export function maybeShowPushBanner(): void {
  if (pushBannerFired) return;
  pushBannerFired = true; // set before any early return so a burst of messages can't double-build
  if (isNtfySubscribed()) return; // already on — nothing to prompt
  try { if (localStorage.getItem(PUSH_BANNER_DISMISSED_KEY) === '1') return; } catch { /* ignore */ }
  if (document.getElementById('hp-push-banner')) return;

  const bar = document.createElement('div');
  bar.id = 'hp-push-banner';
  bar.setAttribute('role', 'status');
  bar.style.cssText =
    'flex-shrink:0;z-index:2147483000;width:100%;box-sizing:border-box;' +
    'display:flex;align-items:center;justify-content:center;gap:14px;' +
    'padding:9px 16px;' +
    'background:var(--accent);color:var(--fg-on-accent);' +
    'font:600 14px/1.25 system-ui,-apple-system,Segoe UI,sans-serif;' +
    'box-shadow:0 2px 10px rgba(0,0,0,.22)';
  const label = document.createElement('span');
  label.textContent = 'Turn on notifications so you see new messages?';
  const enable = document.createElement('button');
  enable.type = 'button';
  enable.textContent = 'Turn on';
  enable.style.cssText =
    'flex-shrink:0;border:0;cursor:pointer;padding:7px 18px;border-radius:8px;' +
    'background:#fff;color:#111;font:700 13px/1 system-ui,sans-serif';
  enable.addEventListener('click', () => {
    // Gesture-synchronous permission prompt, then subscribe to self-hosted ntfy
    // push (no Google/Firebase). Same path as the header bell quick toggle.
    if ('Notification' in window && Notification.permission === 'default') {
      requestNotificationPermissionSync(() => { /* stored by the browser */ });
    }
    subscribeNtfy();
    bar.remove();
  });
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.textContent = '✕';
  dismiss.style.cssText =
    'flex-shrink:0;border:0;cursor:pointer;background:transparent;color:inherit;' +
    'font:700 16px/1 system-ui;opacity:.85;padding:4px 4px';
  dismiss.addEventListener('click', () => {
    try { localStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    bar.remove();
  });
  bar.append(label, enable, dismiss);
  document.body.insertBefore(bar, document.body.firstChild);
}
